import React, { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { SvgProps } from 'react-native-svg';
import {
  DNABox,
  util,
  Iffy,
  DNAButton,
  DNAButtonProps,
  DNADivider,
  DNAText,
  DNAConditionalWrapper,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import RelatedSlidesIcon from 'src/assets/icons/RelatedSlides';
import RelatedSlideProvider from 'src/state/machines/relatedSlide/RelatedSlideProvider';
import SidebarProvider, { SidebarOptions, SidebarOptionsEnum, useSidebar } from './SidebarProvider';

/** Sidebar Content Components */
import SlideNotes from './Panels/SpeakerNotes';
import CustomNotes from './Panels/CustomNotes';
import MeetingNotes from './Panels/MeetingNotes';
import VersionHistory from './Panels/VersionHistory';
import AssociatedFiles from './Panels/AssociatedFiles';
import RelatedSlides from './Panels/RelatedSlides';
import Details from './Panels/Details';
import TextInsertion from './Panels/CustomNotes/TextInsertion';
import Callouts from './Panels/Notations';
import ChevronButton from './ActionButtons/ChevronButton';
import DownloadButton from './ActionButtons/DownloadButton';
import ShareButton from './ActionButtons/ShareButton';

interface TabConfig {
  key: string,
  id: SidebarOptions,
  label: string,
  icon: string | React.FC<SvgProps>,
  customIconName?: string,
  tooltip: string,
  testID?: string,
  component: React.ElementType,
}

export const styles = StyleSheet.create({
  tooltipText: {
    color: colors['color-text-white'],
  },
  mainWrapperClosed: {
    backgroundColor: colors['color-text-white'],
    borderColor: colors['color-gray-80'],
    borderRightWidth: 1,
    width: 56,
  },
  mainWrapperOpen: {
    width: 377,
    borderLeftWidth: 1,
    borderColor: colors['color-gray-80'],
  },
  sidebarButtonsContainer: {
    width: 56,
    borderLeftWidth: 1,
    borderColor: colors['color-gray-80'],
    paddingTop: 10,
  },
});

export const tabConfigs: Record<SidebarOptions, TabConfig> = {
  [SidebarOptionsEnum.speakerNotes]: {
    key: 'slides-button',
    id: SidebarOptionsEnum.speakerNotes,
    label: 'Speaker Notes',
    icon: 'chat-centered-text',
    tooltip: 'Speaker Notes',
    testID: 'content-preview-sidebar-speaker-note',
    component: SlideNotes,
  },
  [SidebarOptionsEnum.customNotes]: {
    key: 'custom-notes-button',
    id: SidebarOptionsEnum.customNotes,
    label: 'My Speaker Notes',
    icon: 'chat-centered',
    tooltip: 'My Speaker Notes',
    testID: 'content-preview-sidebar-my-speaker-notes',
    component: CustomNotes,
  },
  [SidebarOptionsEnum.meetingNotes]: {
    key: 'meeting-notes-button',
    id: SidebarOptionsEnum.meetingNotes,
    label: 'Comments',
    icon: 'note-pencil',
    tooltip: 'Comments',
    testID: 'content-preview-sidebar-comments',
    component: MeetingNotes,
  },
  [SidebarOptionsEnum.versionHistory]: {
    key: 'version-history',
    id: SidebarOptionsEnum.versionHistory,
    label: 'Version History',
    icon: 'stack',
    tooltip: 'Version History',
    testID: 'content-preview-sidebar-version-history',
    component: VersionHistory,
  },
  [SidebarOptionsEnum.associatedFiles]: {
    key: 'associated-files-button',
    id: SidebarOptionsEnum.associatedFiles,
    label: 'Associated Files',
    icon: 'files',
    tooltip: 'Associated Files',
    component: AssociatedFiles,
  },
  [SidebarOptionsEnum.relatedSlides]: {
    key: 'related-slides-button',
    id: SidebarOptionsEnum.relatedSlides,
    label: 'Related Slides',
    icon: RelatedSlidesIcon,
    customIconName: 'related-slides',
    tooltip: 'Related Slides',
    component: RelatedSlides,
  },
  [SidebarOptionsEnum.details]: {
    key: 'details-button',
    id: SidebarOptionsEnum.details,
    label: 'Details',
    icon: 'info',
    tooltip: 'Details',
    testID: 'content-preview-sidebar-details',
    component: Details,
  },
  [SidebarOptionsEnum.textInsertion]: {
    key: 'text-insertion-button',
    id: SidebarOptionsEnum.textInsertion,
    label: 'Insert Text',
    icon: 'article-ny-times',
    tooltip: 'Insert Text',
    testID: 'content-preview-sidebar-text-insertion',
    component: TextInsertion,
  },
  [SidebarOptionsEnum.callouts]: {
    key: 'callouts-button',
    id: SidebarOptionsEnum.callouts,
    label: 'Callouts',
    icon: 'lightbulb',
    tooltip: 'Callouts',
    testID: 'content-preview-sidebar-callouts',
    component: Callouts,
  },
}

const Sidebar: React.FC = () => {
  const {
    availableSidebarOptions,
    selectedSideBarOption,
    setSelectedSideBarOption,
    allowShareOrDownload,
    allowRelatedSlidesProvider,
  } = useSidebar();
  const SelectedSidebar = selectedSideBarOption && tabConfigs[selectedSideBarOption].component;

  const handleSidebarOptionPress = useCallback((item: TabConfig) => {
    setSelectedSideBarOption(item.id);
  }, [setSelectedSideBarOption]);

  const renderSidebarButton = useCallback((config: SidebarOptions) => {
    const item = tabConfigs[config];
    if (!item) return null;

    const iconProp: DNAButtonProps = {};
    if (typeof item.icon === 'string') {
      iconProp.iconLeft = item.icon;
    } else {
      iconProp.customIconLeft = item.icon;
      iconProp.customIconName = item.customIconName;
    }

    return (
      <DNAPopover placement="left" key={item.key} disablePopover={['tabletPWA']}>
        <DNAPopover.Anchor>
          <DNAButton
            appearance={config === selectedSideBarOption ? 'filled' : 'ghost'}
            status={config === selectedSideBarOption ? 'primary' : 'tertiary'}
            onPress={() => handleSidebarOptionPress(item)}
            size="lg"
            {...iconProp}
          />
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText style={styles.tooltipText} numberOfLines={1}>{item?.tooltip}</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    );
  }, [selectedSideBarOption, handleSidebarOptionPress]);

  return (
    <DNAConditionalWrapper
      condition={allowRelatedSlidesProvider}
      wrapper={children => (
        <RelatedSlideProvider>
          {children}
        </RelatedSlideProvider>
      )}
    >
      <DNABox
        testID="content-preview-modal-side-bar"
        appearance="row"
        style={util.mergeStyles(
          undefined,
          styles.mainWrapperClosed,
          [styles.mainWrapperOpen, !!selectedSideBarOption],
        )}
      >
        <DNABox appearance="row">
          <Iffy is={!!selectedSideBarOption}>
            {SelectedSidebar && <SelectedSidebar/>}
          </Iffy>
          <DNABox
            spacing="between"
            alignX="center"
            appearance="col"
            style={styles.sidebarButtonsContainer}
          >
            <DNABox appearance="col" spacing="sm">
              <DNABox alignX="center">
                <ChevronButton />
              </DNABox>
              <DNADivider />
              <DNABox appearance="col" alignX="center" spacing="md">
                {availableSidebarOptions.map(renderSidebarButton)}
              </DNABox>
            </DNABox>
            <Iffy is={allowShareOrDownload}>
              <DNABox appearance="col" alignX="center" alignY="end" spacing="md">
                <ShareButton />
                <DownloadButton />
              </DNABox>
            </Iffy>
          </DNABox>
        </DNABox>
      </DNABox>
    </DNAConditionalWrapper>
  )
}

const SidebarWrapper: React.FC = () => {
  return (
    <SidebarProvider>
      <Sidebar />
    </SidebarProvider>
  )
}

export default SidebarWrapper
